/**
 * @file: useCommonTaskHeader.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { useAlert } from '@euler/app/tv/components/Alert';
import {
  ShareAlertContent,
  ShareAlertContentRef,
} from '@euler/app/tv/components/detail/ShareAlertContent';
import { SubscribeBindQrcodeModalContent } from '@euler/app/tv/components/detail/SubscribeBindQrcodeModalContent';
import type { TaskNavParams } from '@euler/app/tv/pages/TaskFlow';
import { useAppLoading } from '@euler/components/loading/Provider';
import { config, envTypeToTag } from '@euler/config';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useShakeEffect } from '@euler/functions/useShakeEffect';
import { useServiceFactory } from '@euler/services/factory';
import { delayed } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { isMobilePhone } from '@euler/utils/validators';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useRef } from 'react';
import { Keyboard } from 'react-native';
import { BehaviorSubject } from 'rxjs';

export const useShareReport = () => {
  const { rpx } = useResponsiveContext();
  const { detail } = useTaskContext();
  const { smsService } = useServiceFactory();
  const extraInfo = useTaskExtraInfo();
  const shareAlertRef = useRef<ShareAlertContentRef>(null);
  const { shake, shakeStyle } = useShakeEffect(rpx(20));
  const alertAsync = useAlert();
  const appLoading = useAppLoading();

  const onShare = usePersistFn(async () => {
    const loading$ = new BehaviorSubject<boolean | undefined>(undefined);
    const disabled$ = new BehaviorSubject<boolean | undefined>(undefined);
    await alertAsync({
      title: '分享报告给车主',
      message: <ShareAlertContent ref={shareAlertRef} />,
      cancelButtonText: '返回',
      buttonText: '发送',
      contentContainerStyle: shakeStyle,
      loading$,
      disabled$,
      onConfirm: async () => {
        const ref = shareAlertRef.current;
        if (ref == null) {
          return;
        }

        Keyboard.dismiss();

        const mobile = ref.getMobile();
        if (!isMobilePhone(mobile)) {
          delayed(() => {
            shake();
          }, 100);
          return false;
        }

        return await new Promise<boolean | undefined>(resolve => {
          loading$.next(true);
          disabled$.next(true);
          smsService
            .shareReport({
              mobile,
              reportNo: detail.basicInfo.reportNo,
              shareToken: extraInfo?.shareToken ?? '',
              envTag: envTypeToTag(config.environment),
            })
            .then(() => {
              loading$.next(false);
              disabled$.next(false);
              resolve(true);

              appLoading.show({
                type: 'success',
                message: '已发送',
                duration: 1500,
              });
            })
            .catch(error => {
              loading$.next(false);
              disabled$.next(false);
              ref.setError(
                '对不起，在为您发送短信报告时发生了错误，请稍候重试!\n' +
                  error.message,
              );
              resolve(false);
            });
        });
      },
    });
  });

  return onShare;
};

export const useSubscribeBindWithQrcode = () => {
  const { rpx } = useResponsiveContext();
  const { taskNo } = useTaskContext();
  const { taskService } = useServiceFactory();
  const loading = useAppLoading();
  const alertAsync = useAlert();

  return useCallback(async () => {
    const qrcodeImageUrl = await loading.showWithAction(async () => {
      return await taskService.getSubscribeBindQrcodeImageUrl(taskNo, {
        logo: !__DEV__,
        size: rpx(400),
        __fake__: __DEV__,
      });
    });
    await alertAsync({
      title: '',
      buttonText: null,
      message: (
        <SubscribeBindQrcodeModalContent qrcodeImageUrl={qrcodeImageUrl} />
      ),
    });
  }, [alertAsync, loading, rpx, taskNo, taskService]);
};

export const useCommonTaskHeader = () => {
  const { taskManager } = useTaskContext();
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const loading = useAppLoading();

  const onRefresh = useCallback(async () => {
    await loading.showWithAction(async () => {
      await taskManager.refresh();
    });
  }, [loading, taskManager]);

  const onServiceRecordsPress = useCallback(() => {
    navigation.navigate('_serviceRecords', {});
  }, [navigation]);

  const onMediasPress = useCallback(() => {
    navigation.navigate('_vehicleMedias', {});
  }, [navigation]);

  const onMaintenancePlanPress = useCallback(() => {
    navigation.navigate('_maintenancePlan', {});
  }, [navigation]);

  const onTimelinePress = useCallback(() => {
    navigation.navigate('_timeline', {});
  }, [navigation]);

  const onGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onShare = useShareReport();

  const onSubscribeBindPress = useSubscribeBindWithQrcode();

  return {
    onRefresh,
    onServiceRecordsPress,
    onMediasPress,
    onMaintenancePlanPress,
    onSubscribeBindPress,
    onTimelinePress,
    onShare,
    onGoBack,
  };
};
